/**
 * Combate, no molde dos Fire Emblem de Game Boy Advance.
 *
 * As fórmulas são as da série, e a barra de previsão que o jogador vê antes de
 * confirmar um ataque é lida DAQUI — a mesma função que prevê é a que resolve.
 * É o que garante que o número mostrado nunca minta.
 */
import { CONDITIONS, hasEffect, hitPenaltyOf, mergeAfflictions, } from './states.js';
import { attackPower, attackSpeed, avoidRate, critAvoid, critRate, defenseAgainst, hitRate, } from './stats.js';
/** Diferença de velocidade que autoriza o segundo golpe. */
export const DOUBLE_ATTACK_THRESHOLD = 4;
/** Multiplicador do golpe crítico. */
export const CRIT_MULTIPLIER = 3;
/** Multiplicador de arma eficaz — a matadora de cavalaria e suas irmãs. */
export const EFFECTIVE_MULTIPLIER = 3;
/** O que um golpe certeiro deste combatente pode causar. */
export function afflictionsOf(combatant) {
    return mergeAfflictions(combatant.weapon?.inflicts, combatant.inflicts);
}
/**
 * A condição impede este combatente de golpear com a arma que tem?
 *
 * Silêncio cala a arma mágica; paralisia e sono tiram o revide. Quem está
 * paralisado nem chega a atacar — `checkActor` recusa antes —, por isso o
 * impedimento do revide só pesa do lado de quem defende.
 */
function conditionForbids(combatant, counter) {
    const conditions = combatant.conditions ?? [];
    if (conditions.length === 0)
        return false;
    if (combatant.weapon?.magic && hasEffect(conditions, 'sem_magia'))
        return true;
    return counter && hasEffect(conditions, 'sem_contra_ataque');
}
export function triangleOutcome(content, attacker, defender) {
    const a = attacker?.triangleGroup;
    const d = defender?.triangleGroup;
    if (!a || !d || a === d)
        return 'neutro';
    if (content.triangle.beats[a] === d)
        return 'vantagem';
    if (content.triangle.beats[d] === a)
        return 'desvantagem';
    return 'neutro';
}
function isEffective(weapon, target) {
    return Boolean(weapon?.effectiveAgainst?.includes(target.archetype));
}
function inWeaponRange(weapon, distance) {
    if (!weapon)
        return false;
    return distance >= weapon.range[0] && distance <= weapon.range[1];
}
function sideForecast(content, attacker, defender, distance, doubles, counter) {
    const triangle = triangleOutcome(content, attacker.weapon, defender.weapon);
    const canAttack = inWeaponRange(attacker.weapon, distance) &&
        !attacker.weapon?.heals &&
        !conditionForbids(attacker, counter);
    if (!canAttack || !attacker.weapon) {
        return { damage: 0, hit: 0, crit: 0, attacks: 0, canAttack: false, effective: false, triangle };
    }
    const bonus = content.triangle.damageBonus;
    const hitBonus = content.triangle.hitBonus;
    const effective = isEffective(attacker.weapon, defender);
    const triangleDamage = triangle === 'vantagem' ? bonus : triangle === 'desvantagem' ? -bonus : 0;
    const triangleHit = triangle === 'vantagem' ? hitBonus : triangle === 'desvantagem' ? -hitBonus : 0;
    // A arma eficaz triplica o PODER DA ARMA, não o dano final. Triplicar o dano
    // transformaria toda lança em matadora de cavalaria contra unidade frágil.
    const might = effective ? attacker.weapon.might * EFFECTIVE_MULTIPLIER : attacker.weapon.might;
    const power = (attacker.weapon.magic ? attacker.stats.mag : attacker.stats.str) + might;
    const defense = defenseAgainst(defender.stats, attacker.weapon, defender.terrain);
    const damage = Math.max(0, power + triangleDamage - defense);
    const accuracy = hitRate(attacker.stats, attacker.weapon) +
        triangleHit -
        hitPenaltyOf(attacker.conditions) -
        avoidRate(defender.stats, defender.weapon, defender.con, defender.terrain);
    const crit = critRate(attacker.stats, attacker.weapon) - critAvoid(defender.stats);
    const inflicts = afflictionsOf(attacker);
    return {
        damage,
        hit: Math.max(0, Math.min(100, accuracy)),
        crit: Math.max(0, Math.min(100, crit)),
        attacks: doubles ? 2 : 1,
        canAttack: true,
        effective,
        triangle,
        ...(inflicts.length > 0 ? { inflicts } : {}),
    };
}
/** A barra de previsão de combate: tudo o que o jogador vê antes de confirmar. */
export function forecast(content, attacker, defender, distance) {
    const attackerAS = attackSpeed(attacker.stats, attacker.weapon, attacker.con);
    const defenderAS = attackSpeed(defender.stats, defender.weapon, defender.con);
    const attackerCanReach = inWeaponRange(attacker.weapon, distance);
    const defenderCanReach = inWeaponRange(defender.weapon, distance);
    // Só dobra quem consegue atacar de fato — e só quando o outro também está no
    // confronto. Dobrar contra alguém que não pode revidar não muda nada, mas
    // manter a regra clara evita casos estranhos com arqueiro contra espadachim.
    const attackerDoubles = attackerCanReach && attackerAS >= defenderAS + DOUBLE_ATTACK_THRESHOLD;
    const defenderDoubles = defenderCanReach && defenderAS >= attackerAS + DOUBLE_ATTACK_THRESHOLD;
    return {
        attacker: sideForecast(content, attacker, defender, distance, attackerDoubles, false),
        defender: sideForecast(content, defender, attacker, distance, defenderDoubles, true),
        distance,
    };
}
/**
 * Resolve um confronto inteiro.
 *
 * A ordem é a da série: o atacante golpeia, o defensor revida se alcança, e só
 * então quem for rápido o bastante repete. Trocar essa ordem muda quem sobrevive
 * a um confronto apertado, e é uma das coisas que o jogador sente na hora.
 */
export function resolveCombat(content, attacker, defender, distance, rng) {
    const plan = forecast(content, attacker, defender, distance);
    let attackerHp = attacker.currentHp;
    let defenderHp = defender.currentHp;
    let attackerUses = 0;
    let defenderUses = 0;
    const blows = [];
    const afflicted = [];
    /*
     * A aflição sai de um sorteio por golpe certeiro, e só em quem sobrevive a
     * ele. Arma sem aflição não sorteia nada: a sequência de sorteios de toda
     * partida de antes das condições continua exatamente a mesma.
     */
    const afflict = (side, targetAlive) => {
        const view = side === 'atacante' ? plan.attacker : plan.defender;
        if (!targetAlive || !view.inflicts)
            return [];
        const caused = [];
        for (const affliction of view.inflicts) {
            if (!rng.chance(affliction.chance))
                continue;
            caused.push(affliction.condition);
            afflicted.push({
                side: side === 'atacante' ? 'defensor' : 'atacante',
                condition: affliction.condition,
                turns: affliction.turns ?? CONDITIONS[affliction.condition].turns,
            });
        }
        return caused;
    };
    const strike = (side) => {
        const view = side === 'atacante' ? plan.attacker : plan.defender;
        if (!view.canAttack)
            return;
        if (attackerHp <= 0 || defenderHp <= 0)
            return;
        if (side === 'atacante')
            attackerUses++;
        else
            defenderUses++;
        // Acerto usa dois dados, como no GBA: a taxa mostrada é comparada com a
        // média de dois sorteios. Crítico usa um só.
        if (!rng.chance2rn(view.hit)) {
            blows.push({
                side,
                result: 'erro',
                damage: 0,
                targetHpAfter: side === 'atacante' ? defenderHp : attackerHp,
            });
            return;
        }
        const critical = rng.chance(view.crit);
        const damage = critical ? view.damage * CRIT_MULTIPLIER : view.damage;
        const result = critical ? 'critico' : 'acerto';
        if (side === 'atacante') {
            defenderHp = Math.max(0, defenderHp - damage);
            const inflicted = afflict(side, defenderHp > 0);
            blows.push({
                side, result, damage, targetHpAfter: defenderHp,
                ...(inflicted.length > 0 ? { inflicted } : {}),
            });
        }
        else {
            attackerHp = Math.max(0, attackerHp - damage);
            const inflicted = afflict(side, attackerHp > 0);
            blows.push({
                side, result, damage, targetHpAfter: attackerHp,
                ...(inflicted.length > 0 ? { inflicted } : {}),
            });
        }
    };
    strike('atacante');
    strike('defensor');
    if (plan.attacker.attacks > 1)
        strike('atacante');
    if (plan.defender.attacks > 1)
        strike('defensor');
    return {
        blows,
        attackerHpAfter: attackerHp,
        defenderHpAfter: defenderHp,
        attackerUses,
        defenderUses,
        forecast: plan,
        afflicted,
    };
}
/** Cura por cajado: metade da magia mais o poder da arma. */
export function healAmount(healer) {
    if (!healer.weapon?.heals)
        return 0;
    return Math.floor(healer.stats.mag / 2) + healer.weapon.might;
}
/**
 * Cura de um consumível: o valor escrito no item, sem escalar com magia.
 *
 * Um cajado cura melhor nas mãos de quem tem magia — é a recompensa do
 * atributo. Uma poção não: ela vale o mesmo para o cavaleiro e para o mago, e
 * é isso que a descrição do acervo promete ao jogador.
 */
export function itemHealAmount(item) {
    return item.heals ? item.might : 0;
}
//# sourceMappingURL=combat.js.map